import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AssetHeaderComponent, AssetHeaderConfig } from '../asset-header/asset-header.component';
import { AssetFilterComponent, FilterConfig } from '../asset-filter/asset-filter.component';
import { AssetGridComponent } from '../asset-grid/asset-grid.component';
import { FeatureCardData } from '../feature-card/feature-card.component';
import { ModelCardData } from '../model-card/model-card.component';
import { UsecaseCardData } from '../usecase-card/usecase-card.component';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';

@Component({
  selector: 'app-asset-listing-page',
  standalone: true,
  imports: [
    CommonModule,
    HeaderComponent,
    FooterComponent,
    AssetHeaderComponent,
    AssetFilterComponent,
    AssetGridComponent
  ],
  templateUrl: './asset-listing-page.component.html',
  styleUrl: './asset-listing-page.component.scss',
})
export class AssetListingPageComponent {
  // Configuration inputs
  @Input() headerConfig!: AssetHeaderConfig;
  @Input() filterConfig!: FilterConfig;
  @Input() assets: (FeatureCardData | ModelCardData | UsecaseCardData)[] = [];
  @Input() assetType: 'dataset' | 'model' | 'usecase' = 'dataset';
  @Input() searchPlaceholder: string = 'Search for assets';
  @Input() filterSearchPlaceholder: string = 'Search for assets';
  @Input() searchQuery: string = ''; // Add searchQuery input
  @Input() currentPage: number = 1;
  @Input() totalPages: number = 1;
  @Input() viewMode: 'grid' | 'list' | 'table' = 'grid';
  @Input() itemsPerPage: number = 6;
  @Input() showMobileFilterButton: boolean = true;
  @Input() isMobileFilterOpen: boolean = false;
  @Input() isLoggedIn: boolean = false;
  @Input() sortOption: string = 'new-to-old';

  // Event outputs
  @Output() filterChange = new EventEmitter<FilterConfig>();
  @Output() resetFilters = new EventEmitter<void>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() pageChange = new EventEmitter<number>();
  @Output() viewModeChange = new EventEmitter<'grid' | 'list' | 'table'>();
  @Output() itemsPerPageChange = new EventEmitter<number>();
  @Output() openMobileFilter = new EventEmitter<void>();
  @Output() closeMobileFilter = new EventEmitter<void>();
  @Output() bookmarksClick = new EventEmitter<void>();
  @Output() likeClick = new EventEmitter<void>();
  @Output() sortChange = new EventEmitter<string>();

  onFilterChange(filterConfig: FilterConfig): void {
    this.filterChange.emit(filterConfig);
  }

  onResetFilters(): void {
    this.resetFilters.emit();
  }

  onSearchChange(query: string): void {
    this.searchChange.emit(query);
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  onViewModeChange(mode: 'grid' | 'list' | 'table'): void {
    this.viewModeChange.emit(mode);
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPageChange.emit(itemsPerPage);
  }

  onSortChange(sortOption: string): void {
    this.sortChange.emit(sortOption);
  }

  onOpenMobileFilter(): void {
    this.openMobileFilter.emit();
  }

  onCloseMobileFilter(): void {
    this.closeMobileFilter.emit();
  }

  // Get dynamic CSS class based on asset type
  get pageClass(): string {
    return `${this.assetType}s-page`;
  }

  get contentClass(): string {
    return `${this.assetType}s-page--content`;
  }

  get mainClass(): string {
    return `${this.assetType}s-page--main`;
  }

  get containerClass(): string {
    return `${this.assetType}s-page--container`;
  }

  get sidebarClass(): string {
    return `${this.assetType}s-page--sidebar`;
  }

  get gridClass(): string {
    return `${this.assetType}s-page--grid`;
  }

  get mobileFilterOverlayClass(): string {
    return `${this.assetType}s-page--mobile-filter-overlay`;
  }

  get mobileFilterModalClass(): string {
    return `${this.assetType}s-page--mobile-filter-modal`;
  }

  get mobileFilterHeaderClass(): string {
    return `${this.assetType}s-page--mobile-filter-header`;
  }

  get mobileFilterTitleClass(): string {
    return `${this.assetType}s-page--mobile-filter-title`;
  }

  get mobileFilterCloseClass(): string {
    return `${this.assetType}s-page--mobile-filter-close`;
  }

  get mobileFilterContentClass(): string {
    return `${this.assetType}s-page--mobile-filter-content`;
  }

  get mobileFilterFooterClass(): string {
    return `${this.assetType}s-page--mobile-filter-footer`;
  }

  get mobileFilterResetClass(): string {
    return `${this.assetType}s-page--mobile-filter-reset`;
  }

  get mobileFilterApplyClass(): string {
    return `${this.assetType}s-page--mobile-filter-apply`;
  }

  get mobileFilterTitle(): string {
    const assetTypeMap: { [key: string]: string } = {
      'dataset': 'Datasets',
      'model': 'Models',
      'usecase': 'Usecases'
    };
    return `Filter ${assetTypeMap[this.assetType] || 'Assets'}`;
  }
}
